import type { ConversationRunPlace } from '@/hooks/use-agent-runtime';
import { resolvePersistedAgent } from '@/lib/desktop-shell/cloud-agents';
import { conversationWorktree } from '@/lib/desktop-shell/conversation-worktree';
import { desktopWorkspaceName } from '@/lib/desktop-shell/personal-workspace';
import type { DesktopShellState } from '@/lib/desktop-shell/store';
import { runtimeAgentFor } from '@/lib/desktop-shell/use-desktop-agent-connection';
import type { NativeAgent, NativeWorkspaceEnvironment } from '@/lib/native-desktop';

/**
 * Where a conversation that is not shown runs its turns now, from its cached copy (which follows
 * it: `ConversationScope.place`): its agent, its folder (its worktree once made, its sandbox's
 * once started) and, in a worktree, its repository's environment. `null` while it has no agent or
 * no folder.
 */
export async function conversationRunPlace(
  state: Pick<
    DesktopShellState,
    'conversationCache' | 'syncedThreads' | 'syncedWorkspaces' | 'personalWorkspaceRoot'
  >,
  chatId: string,
  agents: readonly NativeAgent[],
  readEnvironment: (root: string) => Promise<NativeWorkspaceEnvironment | null>,
): Promise<ConversationRunPlace | null> {
  const cached = state.conversationCache[chatId];
  if (cached === undefined || cached.workspaceRoot === null || cached.agentId === undefined)
    return null;
  const agent = resolvePersistedAgent(agents, cached.agentId);
  if (agent === undefined) return null;
  const { workspaceRoot } = cached;
  const runtime = cached.runtime ?? 'local';
  if (runtime === 'cloud') return { agent, workspaceRoot, cloud: true, persistenceWorkspace: null };
  const sourceRoot =
    runtime === 'worktree'
      ? (conversationWorktree(state, chatId, workspaceRoot)?.sourceRoot ?? null)
      : null;
  const environment =
    sourceRoot === null ? null : await readEnvironment(sourceRoot).catch(() => null);
  return {
    agent: runtimeAgentFor(agent, runtime, environment),
    workspaceRoot,
    cloud: false,
    persistenceWorkspace: {
      name: desktopWorkspaceName(workspaceRoot, state.personalWorkspaceRoot),
      runtime: runtime === 'worktree' ? 'worktree' : 'local',
    },
  };
}
